import {byProp} from '@lib/utils';
import {createSelector, Dictionary} from '@reduxjs/toolkit';
import {
  getConnectionEntities,
  getAllConnectedUsers,
  getIsLoadingConnections,
  getIsLoadingConnectionSuggestions,
  getIsSendingConnectionSuggestionsFeedback,
  getIsConnecting,
  getAcceptedConnectedUsers,
  getAllWorkdaysForConnections,
} from '../../data/connections';
import {Connection} from '../../data/entities';
import {ConnectionUserWithWorkday, ConnectionsViewMode} from './types';
import filter from 'lodash/filter';
import matches from 'lodash/matches';
import partition from 'lodash/partition';
import {groupConnectedUsersByStatus} from './utils';

export const getConnectedUsersGroupedByStatusForWeb = createSelector(
  getConnectionEntities,
  getAllConnectedUsers,
  (connections, connectedUsers) => {
    const getConnection = (conns: Dictionary<Connection>, connId?: string) => {
      return conns[connId ?? ''];
    };

    const acceptedConnections = connectedUsers
      .filter((user) => {
        const connection = getConnection(connections, user.connectionId);
        return !!connection && connection.status === 'Accepted';
      })
      .sort(byProp('name'));

    const incomingConnections = connectedUsers
      .filter((user) => {
        const connection = getConnection(connections, user.connectionId);
        return !!connection && connection.status === 'Pending' && connection.direction === 'Incoming';
      })
      .sort(byProp('name'));

    const outgoingConnections = connectedUsers
      .filter((user) => {
        const connection = getConnection(connections, user.connectionId);
        return !!connection && connection.status === 'Pending' && connection.direction === 'Outgoing';
      })
      .sort(byProp('name'));

    return {acceptedConnections, incomingConnections, outgoingConnections};
  },
);

export const getConnectedUsersGroupedByStatus = createSelector(
  getConnectionEntities,
  getAllConnectedUsers,
  (connections, connectedUsers) => {
    return groupConnectedUsersByStatus(connections, connectedUsers);
  },
);

export const getConnectionsViewMode = createSelector(
  getIsLoadingConnections,
  getAllConnectedUsers,
  (isLoading, connections): ConnectionsViewMode => {
    if (isLoading) {
      return 'Loading';
    }
    return connections.length > 0 ? 'Connections' : 'NoConnections';
  },
);

export const getIsLoadingConnectionSuggestionsList = createSelector(
  getIsLoadingConnections,
  getIsLoadingConnectionSuggestions,
  getIsSendingConnectionSuggestionsFeedback,
  getIsConnecting,
  (isLoadingConnections, isLoadingConnectionSuggestions, isSendingConnectionSuggestionsFeedback, isConnecting) =>
    isLoadingConnections || isLoadingConnectionSuggestions || isSendingConnectionSuggestionsFeedback || isConnecting,
);

export const getConnectedUsersWithAndWithoutBookings = createSelector(
  getAllWorkdaysForConnections,
  getAcceptedConnectedUsers,
  (workdaysForConnections, connectedUsers) => {
    const officeDaysForConnections = workdaysForConnections.filter(({status}) => status === 'OfficeDay');

    const allConnections = connectedUsers
      .map(
        (connectedUser): ConnectionUserWithWorkday => ({
          ...connectedUser,
          workdays: filter(officeDaysForConnections, matches({userId: connectedUser.id})),
        }),
      )
      .sort((a, b) => {
        const displayNameA = a.name || a.email;
        const displayNameB = b.name || b.email;

        return displayNameA.localeCompare(displayNameB);
      });

    const [connectionsWithWorkdays, connectionsWithoutWorkdays] = partition(
      allConnections,
      ({workdays}) => workdays.length,
    );

    return {allConnections, connectionsWithWorkdays, connectionsWithoutWorkdays};
  },
);
